import {
    AreaConfig,
    Lease,
    LeaseStatus,
    ModelMap,
    traverse,
    Unit,
    UnitConfig,
} from '../../../../models'
import { VersionedTree } from '../../../../hooks/useInfrastructure/types'

export interface LeaseBoardArea {
    id: number
    areaLabel: string
    areaConfigName: string
}

export interface LeaseBoardUnit {
    id: number
    name: string
    folderLabel: string
    unitConfigId: number
    unitConfigName: string
    areas: LeaseBoardArea[]
}

const STATUS_RANK: Record<number, number> = {
    [LeaseStatus.CURRENT]: 0,
    [LeaseStatus.NOTICE]: 1,
    [LeaseStatus.FUTURE]: 2,
    [LeaseStatus.APPLICANT]: 3,
    [LeaseStatus.CANCELLED]: 4,
    [LeaseStatus.PAST]: 5,
}

export function leasePlaceLabels(
    lease: Lease,
    unit: Unit | undefined,
    areaConfigName?: string,
): { location: string; unitName: string; bed: string } {
    const place = lease.location
    if (place) {
        return {
            location: `${place.folder_path ?? ''}${place.folder_name ?? ''}`,
            unitName: place.unit_name ?? '',
            bed: `${place.area_config_name ?? ''} ${
                place.area_label ?? ''
            }`.trim(),
        }
    }
    return {
        location: `${unit?.folder?.path ?? ''}${unit?.folder?.name ?? ''}`,
        unitName: unit?.name ?? '',
        bed: `${areaConfigName ?? '?'} ${lease.area?.area_label ?? ''}`.trim(),
    }
}

export function leaseCanTransfer(lease: Lease, now = new Date()): boolean {
    return new Date(lease.end_date) > now && lease.status !== LeaseStatus.PAST
}

/** Transfer ends this lease and creates a new one on the destination bed. */
export function leaseTransferCreatesNewLease(lease: Lease): boolean {
    return (
        lease.status === LeaseStatus.CURRENT ||
        lease.status === LeaseStatus.NOTICE
    )
}

export function compareLeasesForBed(a: Lease, b: Lease): number {
    const rank = (STATUS_RANK[a.status] ?? 99) - (STATUS_RANK[b.status] ?? 99)
    if (rank !== 0) return rank
    return new Date(b.start_date).getTime() - new Date(a.start_date).getTime()
}

export function leasesOnArea(leases: Lease[], areaId: number): Lease[] {
    return leases
        .filter((lease) => lease.area?.id === areaId)
        .slice()
        .sort(compareLeasesForBed)
}

/** Leases that still occupy a bed and should be named before a move. */
export function activeOccupantsOnArea(
    leases: Lease[],
    areaId: number,
    movingLeaseId: number,
): Lease[] {
    return leasesOnArea(leases, areaId).filter(
        (lease) =>
            lease.id !== movingLeaseId &&
            lease.status !== LeaseStatus.PAST &&
            lease.status !== LeaseStatus.CANCELLED,
    )
}

function areaConfigId(area: {
    area_config: number | AreaConfig | null | undefined
    area_config_id?: number | null
}): number | undefined {
    const raw = area.area_config as number | AreaConfig | null | undefined
    if (typeof raw === 'number') return raw
    if (raw && typeof raw === 'object') return raw.id
    return typeof area.area_config_id === 'number'
        ? area.area_config_id
        : undefined
}

function configIsOccupiable(config: AreaConfig | undefined): boolean {
    if (!config) return false
    const occupiable = config.occupiable as unknown
    return (
        occupiable === true ||
        occupiable === 1 ||
        occupiable === '1' ||
        occupiable === 'true'
    )
}

function areaConfigFor(
    area: {
        area_config: number | AreaConfig | null | undefined
        area_config_id?: number | null
    },
    areaConfigMap: ModelMap<AreaConfig>,
): AreaConfig | undefined {
    const raw = area.area_config as number | AreaConfig | null | undefined
    if (raw && typeof raw === 'object' && configIsOccupiable(raw)) {
        return raw
    }
    const id = areaConfigId(area)
    return id == null ? undefined : areaConfigMap[id]
}

export interface OccupiableAreaRow {
    id: number
    area_label: string
    unit: number
    unit_name?: string | null
    unit_config?: number | null
    unit_config_name?: string | null
    area_config_name?: string | null
}

function unitConfigFromInfrastructure(
    unit: Unit | undefined,
): {
    id: number
    name?: string
} {
    const raw = unit?.unit_config as
        | number
        | { id?: number; name?: string }
        | null
        | undefined
    if (raw && typeof raw === 'object') {
        return { id: raw.id ?? 0, name: raw.name }
    }
    const id =
        typeof raw === 'number'
            ? raw
            : (unit as { unit_config_id?: number } | undefined)?.unit_config_id
    return { id: id ?? 0 }
}

/** Units that have at least one area the server marked occupiable. */
export function buildLeaseBoardUnitsFromOccupiableAreas(
    areas: OccupiableAreaRow[],
    tree: VersionedTree,
    unitConfigMap: ModelMap<UnitConfig>,
): LeaseBoardUnit[] {
    const unitById = new Map<number, Unit>()
    if (tree?.root) {
        traverse(tree.root, (folder) => {
            folder.units?.forEach((unit) => {
                unitById.set(unit.id, unit)
            })
        })
    }

    const grouped = new Map<number, LeaseBoardUnit>()
    areas.forEach((area) => {
        if (area?.id == null || area.unit == null) return
        const infra = unitById.get(area.unit)
        let board = grouped.get(area.unit)
        if (!board) {
            const fromTree = unitConfigFromInfrastructure(infra)
            const unitConfigId =
                (typeof area.unit_config === 'number'
                    ? area.unit_config
                    : undefined) ?? fromTree.id
            const unitConfigName =
                area.unit_config_name ||
                fromTree.name ||
                unitConfigMap[unitConfigId]?.name ||
                'Unit type'
            board = {
                id: area.unit,
                name: infra?.name || area.unit_name || `Unit ${area.unit}`,
                folderLabel: infra
                    ? `${infra.folder?.path ?? ''}${infra.folder?.name ?? ''}`
                    : '',
                unitConfigId,
                unitConfigName,
                areas: [],
            }
            grouped.set(area.unit, board)
        }
        board.areas.push({
            id: area.id,
            areaLabel: area.area_label ?? '',
            areaConfigName: area.area_config_name || 'Bed',
        })
    })

    const units = Array.from(grouped.values())
    units.forEach((unit) => {
        unit.areas.sort((a, b) =>
            `${a.areaConfigName} ${a.areaLabel}`.localeCompare(
                `${b.areaConfigName} ${b.areaLabel}`,
                undefined,
                { numeric: true },
            ),
        )
    })
    units.sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { numeric: true }),
    )
    return units
}

export function buildLeaseBoardUnits(
    tree: VersionedTree,
    areaConfigMap: ModelMap<AreaConfig>,
    unitConfigMap: ModelMap<UnitConfig>,
): LeaseBoardUnit[] {
    const units: LeaseBoardUnit[] = []
    if (!tree?.root) return units

    traverse(tree.root, (folder) => {
        folder.units?.forEach((unit) => {
            const areas = (unit.areas ?? [])
                .map((area) => {
                    const config = areaConfigFor(area, areaConfigMap)
                    if (!configIsOccupiable(config)) return null
                    return {
                        id: area.id,
                        areaLabel: area.area_label,
                        areaConfigName:
                            config?.name ||
                            (area as { area_config_name?: string })
                                .area_config_name ||
                            'Bed',
                    }
                })
                .filter((area): area is LeaseBoardArea => area != null)
                .sort((a, b) =>
                    `${a.areaConfigName} ${a.areaLabel}`.localeCompare(
                        `${b.areaConfigName} ${b.areaLabel}`,
                        undefined,
                        { numeric: true },
                    ),
                )
            if (areas.length === 0) return

            units.push({
                id: unit.id,
                name: unit.name,
                folderLabel: `${unit.folder?.path ?? ''}${
                    unit.folder?.name ?? ''
                }`,
                unitConfigId: unit.unit_config,
                unitConfigName:
                    unitConfigMap[unit.unit_config]?.name || 'Floor plan',
                areas,
            })
        })
    })

    units.sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { numeric: true }),
    )
    return units
}

export function unitIsInLocationSelection(
    selection: { [id: number]: Unit; length: number },
    unitId: number,
): boolean {
    return selection.length === 0 || selection[unitId] !== undefined
}

export function boardUnitMatchesSearch(
    unit: LeaseBoardUnit,
    occupantText: string,
    searchValue: string,
): boolean {
    const needle = searchValue.trim().toLowerCase()
    if (!needle) return true
    if (unit.name.toLowerCase().startsWith(needle)) return true
    if (unit.folderLabel.toLowerCase().startsWith(needle)) return true
    if (unit.unitConfigName.toLowerCase().includes(needle)) return true
    if (
        unit.areas.some((area) =>
            `${area.areaConfigName} ${area.areaLabel}`
                .toLowerCase()
                .includes(needle),
        )
    ) {
        return true
    }
    return occupantText.toLowerCase().includes(needle)
}
